// Phase 6 — Track D T-D3. /alerts route.

import * as React from 'react';
import { copy } from '@dailydeck/shared/copy';
import { useAlerts, type AlertsPriorityFilter } from '../../hooks/useAlerts.js';
import { AlertRow } from './AlertRow.js';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../../components/ui/Card.js';
import { Button } from '../../components/ui/Button.js';
import { SkeletonRow } from '../../components/ui/Skeleton.js';
import { EmptyState } from '../../components/ui/EmptyState.js';
import { cn } from '../../lib/cn.js';

const FILTERS: readonly AlertsPriorityFilter[] = ['all', 'critical', 'important', 'info'];

interface PriorityFilterProps {
  value: AlertsPriorityFilter;
  onChange: (next: AlertsPriorityFilter) => void;
}

function PriorityFilter({ value, onChange }: PriorityFilterProps): JSX.Element {
  return (
    <div role="group" aria-label="Filter by priority" className="flex flex-wrap items-center gap-2">
      {FILTERS.map((p) => {
        const isActive = value === p;
        return (
          <button
            key={p}
            type="button"
            aria-pressed={isActive}
            onClick={() => onChange(p)}
            className={cn(
              'rounded-full border px-3 py-1 text-caption transition-colors duration-fast',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-default focus-visible:ring-offset-2 focus-visible:ring-offset-surface-base',
              isActive
                ? 'bg-accent-default border-accent-default text-text-on-accent'
                : 'bg-surface-raised border-border-default text-text-primary hover:bg-accent-subtle',
            )}
          >
            {copy.alerts.filters[p]}
          </button>
        );
      })}
    </div>
  );
}

export function Alerts(): JSX.Element {
  React.useEffect(() => {
    document.title = `DailyDeck — ${copy.alerts.pageTitle}`;
  }, []);

  const [priority, setPriority] = React.useState<AlertsPriorityFilter>('all');
  const query = useAlerts({ priority });

  return (
    <div className="space-y-6" data-testid="alerts-route">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-title-1 font-semibold text-text-primary">{copy.alerts.pageTitle}</h1>
          <p className="text-body text-text-secondary mt-1">{copy.alerts.pageSubtitle}</p>
        </div>
        <PriorityFilter value={priority} onChange={setPriority} />
      </header>

      {query.isPending && (
        <div className="rounded-lg border border-border-subtle bg-surface-raised" aria-label={copy.alerts.loadingLabel}>
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </div>
      )}

      {query.isError && (
        <Card data-testid="alerts-error">
          <CardHeader>
            <CardTitle>{copy.alerts.errorTitle}</CardTitle>
            <CardDescription>{copy.errors.network_unavailable}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-body text-text-secondary">{copy.alerts.errorBody}</p>
          </CardContent>
          <CardFooter>
            <Button variant="secondary" onClick={() => void query.refetch()}>
              {copy.alerts.retry}
            </Button>
          </CardFooter>
        </Card>
      )}

      {query.isSuccess && query.data.length === 0 && (
        <EmptyState
          title={copy.alerts.emptyTitle}
          description={copy.alerts.emptyBody}
        />
      )}

      {query.isSuccess && query.data.length > 0 && (
        <div className="rounded-lg border border-border-subtle bg-surface-raised overflow-hidden" data-testid="alerts-list">
          {query.data.map((alert, idx) => (
            <AlertRow key={alert.id ?? `${alert.alertTypeId}:${idx}`} alert={alert} />
          ))}
        </div>
      )}
    </div>
  );
}
